import { fireTrackAnalytics } from '@atlassian/jira-analytics-web-react/src';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { getFeatureAnalyticsAttributes } from '@atlassian/jira-software-feature-analytics';
import { View } from '@atlassian/jira-software-view-settings/src/common/types/constant';
import { getViewSettings } from '@atlassian/jira-software-view-settings/src/services/local-storage';
import { isVersionPanelOpen } from '../../../../services/panel';
import type { BacklogView, RefreshDataRequestAction } from '../../../actions/app';
import { getAttributes } from '../../../analytics';
import type { State } from '../../../reducers/types';
import { getIsCompanyManaged, getProjectIds, getRapidViewId } from '../../../selectors/app';
import { getBaseLevelCardTypes } from '../../../selectors/card-type';
import {
	getCards,
	getOrderedLabels,
	getVisibleCardIds,
	getVisibleSprintCardLists,
} from '../../../selectors/cards';
import { getAllFeatures } from '../../../selectors/config';
import { getEpicPanel } from '../../../selectors/panels';
import type { Dependencies } from '../../../types';

export const backlogView =
	(eventPrefix: string) => (preState: State, state: State, action: BacklogView) => {
		const numCards = Object.keys(getCards(state)).length;
		const { analyticsEvent } = action.meta;

		const visibleCardIds = getVisibleCardIds(state);
		const orderedLabels = getOrderedLabels(state);

		const sprints = getVisibleSprintCardLists(state);
		const unscheduledSprints = sprints.filter(
			(sprint) => !sprint.sprintData?.startDate && !sprint.sprintData?.endDate,
		);

		const isCompanyManaged = getIsCompanyManaged(state);
		const boardId = Number(getRapidViewId(state));
		const isQuickFiltersBarToggled = getViewSettings({
			boardId,
			view: View.BACKLOG,
		})?.showQuickFilters;

		const cmpAttributes = isCompanyManaged && {
			isQuickFiltersBarToggled,
		};

		const extra = {
			numCards,
			numSprints: sprints.length,
			numUnscheduledSprints: unscheduledSprints.length,
			hasFilteredIssues: visibleCardIds.length !== numCards,
			numLabels: orderedLabels.length,
			issueResultProjectIds: getProjectIds(state),
		};
		const featureAttributes = getFeatureAnalyticsAttributes(getAllFeatures(state));

		fireTrackAnalytics(analyticsEvent, {
			action: 'viewed',
			attributes: {
				...featureAttributes,
				isEpicPanelOpen: getEpicPanel(state),
				isVersionPanelOpen: isVersionPanelOpen(),
				...extra,
				...cmpAttributes,
			},
		});

		return {
			name: `${eventPrefix}.view`,
			data: {
				...extra,
			},
		};
	};

export const backlogLoadAppDataFailure = (eventPrefix: string) => () => ({
	name: `${eventPrefix}.load-app-data.failure`,
	data: {},
});

export const backlogLoadConfigDataFailure = (eventPrefix: string) => () => ({
	name: `${eventPrefix}.load-config-data.failure`,
	data: {},
});

export const refreshDataRequest =
	(eventPrefix: string) =>
	(
		preState: State,
		state: State,
		action: RefreshDataRequestAction,
		dependencies?: Dependencies,
	) => {
		const { createAnalyticsEvent } = dependencies || {};

		if (createAnalyticsEvent) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'backlog refreshed', {
				...getAttributes(state),
				source: action.payload.source,
			});
		}

		return {
			name: `${eventPrefix}.refresh`,
			data: {
				source: action.payload.source,
			},
		};
	};

export const backlogLoadConfigDataSuccess =
	(eventPrefix: string) => (preState: State, state: State) => ({
		name: `${eventPrefix}.load-config-data.success`,
		data: {
			numIssueTypes: getBaseLevelCardTypes(state).length,
		},
	});
